import NiceModal from '@ebay/nice-modal-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useParams } from 'react-router';
import { getBudgetChangeOrderList } from '@/api/Sherpa/ChangeOrder';
import { AccordionItem } from '@/components/UI/Accordion';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import { TableHeader } from '@/features/ChangeOrder/Summary/TableHeader';
import type { ReactSelectOption } from '@/types/Constants';
import Table from '../Table';
import { AddBudgetModal } from './AddBudget';
import { columns } from './columns';
import { FiltersModal } from './FilterBy';

export function ChangeOrderBudget({
  totalImpact,
  approverOptions,
  submitterOptions,
}: {
  totalImpact: number;
  submitterOptions: ReactSelectOption[];
  approverOptions: ReactSelectOption[];
}) {
  const queryClient = useQueryClient();
  const { projectId = '' } = useParams();
  const [appliedFilters, setAppliedFilters] = useState<FieldValues>({});
  const [displayFilters, setDisplayFilters] = useState<FieldValues>({});
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 5,
  });

  //Tanstack: version 5 query implementation
  const { data, isLoading } = useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_BUDGET, { ...appliedFilters, ...pagination }],
    queryFn: () =>
      getBudgetChangeOrderList({
        ...appliedFilters,
        refType: 'project',
        refId: Number(projectId),
        page: pagination.page,
        perPage: pagination.perPage,
      }),
  });

  const handleFiltersOpen = (event: React.MouseEvent) => {
    event.stopPropagation();
    NiceModal.show(FiltersModal, {
      submitterOptions,
      approverOptions,
      displayFilters,
      setAppliedFilters,
      setDisplayFilters,
    });
  };

  const handleAdd = (event: React.MouseEvent) => {
    event.stopPropagation();
    NiceModal.show(AddBudgetModal, {
      projectId,
      onSubmitted: () => {
        //Tanstack: version 5 query implementation
        queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_BUDGET] });
        queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_SUMMARY] });
      },
    });
  };

  const totalPage = Math.max(1, Math.ceil((data?.meta.total || 0) / pagination.perPage));

  const perPageHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setPagination((prevState) => ({
      ...prevState,
      page: 1,
      perPage: Number(event.target.value),
    }));
  };

  const pageChangeHandler = (direction: string) => {
    if (direction === 'prev') {
      setPagination((prevState) => ({
        ...prevState,
        page: Math.max(1, prevState.page - 1),
      }));
    } else {
      setPagination((prevState) => ({
        ...prevState,
        page: Math.min(prevState.page + 1, totalPage),
      }));
    }
  };

  const paginationProps = {
    page: pagination.page,
    perPage: pagination.perPage,
    pageSize: totalPage,
    perPageHandler,
    pageChangeHandler,
  };

  return (
    <AccordionItem
      id='budget'
      rootClassName='bg-white rounded-md shadow-lg border-none'
      customClassName='p-0 pb-5'
      title={
        <TableHeader
          title='Budget'
          appliedFilters={appliedFilters}
          totalApproved={data?.meta.approved || 0}
          totalRequested={data?.meta.total || 0}
          impactValue={totalImpact}
          impactType='money'
          onFiltersOpen={handleFiltersOpen}
          onAdd={handleAdd}
        />
      }
    >
      <Table
        cols={columns}
        data={data?.list || []}
        fetching={isLoading}
        enableColumnResizing={false}
        wrapperClassName='!w-auto'
        tableClassName='table-fixed !w-full'
        pagination={paginationProps}
      />
    </AccordionItem>
  );
}
